<template>
  <div class="d-flex p-2 min-vh-100">
    <div class="col-lg-6 d-none d-lg-flex align-items-center justify-content-center">
      <div class="w-100 h-100 rounded-4 shadow-lg" :style="{
        backgroundImage:
          'url(' +
          require('@/assets/img/illustrations/illustration-signin.jpg') +
          ')',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '10px'
      }"></div>
    </div>

    <!-- Right side form -->
    <div class="col-xl-5 col-lg-6 col-md-8 ms-auto me-auto">
      <!-- <div class="card shadow-lg rounded-4 border-0 p-4"> -->
      <div class="p-4">
        <!-- Header -->
        <div class="text-center mb-3">
          <h3 class="fw-bold text-success mb-1">
            {{ currentStep === 'signup' ? 'Create Account - ChurchScheduling.com' : 'Verify Your Email' }}
          </h3>
          <p class="text-muted">
            {{ currentStep === 'signup' ? 'Join us and get started in minutes' : 'Enter the OTP sent to your email' }}
          </p>
        </div>

        <div v-if="invitationLoading" class="card-body text-center py-5" role="status">
          <span class="spinner-border spinner-border-sm text-success me-2" aria-hidden="true"></span>
          Loading your invitation…
        </div>
        <div v-else-if="invitationError" class="card-body text-center py-5" role="alert">
          <p class="text-danger">{{ invitationError }}</p>
          <router-link :to="signInTarget" class="text-success fw-bold">Go to sign in</router-link>
        </div>
        <!-- Signup Form -->
        <div class="card-body" v-else-if="currentStep === 'signup'">
          <form role="form" @submit.prevent="handleSubmit">
            <div
              v-if="hasLocationQuery"
              class="signup-location-context mb-4"
              aria-live="polite"
              :aria-busy="locationLoading"
            >
              <div v-if="locationLoading" class="d-flex align-items-center gap-2 text-muted" role="status">
                <span class="spinner-border spinner-border-sm text-success" aria-hidden="true"></span>
                <span>Loading organization and Location details...</span>
              </div>
              <div v-else-if="locationDetails" class="d-flex align-items-center gap-3">
                <div class="signup-location-icon" aria-hidden="true">
                  <i class="material-icons">church</i>
                </div>
                <div class="signup-location-copy">
                  <p class="signup-location-eyebrow mb-1">You're joining</p>
                  <h5 class="mb-1">{{ locationDetails.organization_name || 'Organization' }}</h5>
                  <p class="signup-location-name mb-0">
                    <i class="material-icons" aria-hidden="true">place</i>
                    {{ locationDetails.name || 'Location' }}
                  </p>
                </div>
              </div>
              <p v-else class="text-danger mb-0" role="alert">
                {{ locationLoadError || 'Unable to load the organization and Location for this sign-up link.' }}
              </p>
            </div>
            <div v-if="invitation" class="signup-invitation-note mb-4">
              <div class="signup-invitation-note-icon"><i class="material-icons" aria-hidden="true">mail</i></div>
              <div>
                <p class="mb-1"><strong>{{ invitation.inviter_name || 'A fellow volunteer' }}</strong> thought you might enjoy serving here.</p>
                <p v-if="invitation.custom_message" class="mb-0 signup-invitation-personal-note">{{ invitation.custom_message }}</p>
              </div>
            </div>
            <!-- Firstname + Lastname -->
            <div class="row">
              <div class="col-6 mb-3">
                <material-input id="firstname" type="text" label="First Name" v-model="form.firstname" size="lg"
                  prepend-icon="fa fa-user" is-required />
              </div>
              <div class="col-6 mb-3">
                <material-input id="lastname" type="text" label="Last Name" v-model="form.lastname" size="lg"
                  prepend-icon="fa fa-user" />
              </div>
            </div>

            <!-- Email -->
            <div class="mb-3">
              <material-input id="email" type="email" label="Email" v-model="form.email" size="lg"
                prepend-icon="fa fa-envelope" is-required :error="!!emailError" @blur="validateEmail" />
              <small v-if="emailError" class="text-danger">{{ emailError }}</small>
            </div>

            <!-- Mobile Phone -->
            <div class="mb-3">
              <material-input id="mobilephone" type="text" label="Mobile Phone" v-model="form.mobilephone" size="lg"
                prepend-icon="fa fa-phone" :error="!!mobilePhoneError" @blur="validateAndFormatMobilePhone" />
              <small v-if="mobilePhoneError" class="text-danger">{{ mobilePhoneError }}</small>
            </div>

            <!-- Country Dropdown -->
            <div class="mb-3">
              <material-select id="country" label="Country" :options="countryOptions" v-model="form.country" size="lg"
                is-required :read-only="hasLocationQuery" />
            </div>

            <!-- Time Zone Dropdown -->
            <div class="mb-3">
              <material-select id="time_zone" label="Time Zone" :options="timezoneOptions" v-model="form.time_zone"
                size="lg" is-required :read-only="hasLocationQuery" />
            </div>

            <div class="mb-3">
              <material-select id="registrationtype" label="Registration Type" :options="registrationTypeOptions"
                v-model="form.registrationtype" size="lg" is-required />
            </div>
            <div v-if="form.registrationtype === '1' && validLocationId !== null" class="mb-3 p-3 rounded"
              style="background:#f0fdf4;border:1px solid #bbf7d0;">
              <fieldset class="signup-skill-fieldset">
                <legend class="form-label text-sm fw-semibold">Serve Roles/Skills of Interest</legend>
                <p class="text-muted text-sm mb-2">
                  Select the roles and skills you're interested in. Preselected items are locked and cannot be removed.
                </p>
                <div class="signup-skill-options" role="group" aria-label="Serve Roles/Skills of Interest">
                  <div v-for="skill in publicSkills" :key="skill.skillid" class="form-check signup-skill-option">
                    <input
                      :id="`signup-skill-${skill.skillid}`"
                      v-model="form.selectedSkillIds"
                      class="form-check-input"
                      type="checkbox"
                      :value="skill.skillid"
                      :disabled="skill.preselect"
                    />
                    <label :for="`signup-skill-${skill.skillid}`" class="form-check-label signup-skill-label">
                      <span>
                        <span class="fw-semibold">{{ skill.skillname }}</span>
                        <small v-if="skill.description" class="d-block text-muted">{{ skill.description }}</small>
                      </span>
                      <span v-if="skill.invite_suggested" class="badge rounded-pill bg-primary-subtle text-primary ms-auto">
                        Suggested
                      </span>
                      <span v-else-if="skill.preselect" class="badge rounded-pill bg-success-subtle text-success ms-auto">
                        Preselected
                      </span>
                    </label>
                  </div>
                </div>
              </fieldset>
              <small v-if="publicSkills.length === 0" class="text-muted">No publicly viewable skills are available for this Location.</small>
            </div>

            <!-- Registration Details (shown only when Setup new organization is selected) -->
            <div v-if="form.registrationtype === '2'" class="mb-3 p-3 rounded" style="background:#f0fdf4;border:1px solid #bbf7d0;">
              <label class="form-label fw-semibold text-success d-flex align-items-center gap-1 mb-2">
                <i class="material-icons" style="font-size:18px;">edit_note</i>
                Registration Details <span class="text-danger">*</span>
              </label>
              <p class="text-muted text-sm mb-3" style="line-height:1.5;">Please provide as much detail as possible about your organization. This helps us personalize your setup experience.</p>
              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">How did you hear about churchscheduling.com?</label>
                <material-select id="heardAboutUs" :options="heardAboutOptions" v-model="form.heardAboutUs" size="lg" />
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">What is the name of your organization/church?</label>
                <input type="text" class="form-control" v-model="form.orgName" placeholder="e.g. Grace Community Church" />
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">What types of events or services do you schedule volunteers for?</label>
                <textarea class="form-control" v-model="form.eventTypes" rows="2" placeholder="e.g. Sunday morning service, Wednesday Bible study, monthly outreach events..."></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">How do you currently manage your volunteer schedules?</label>
                <textarea class="form-control" v-model="form.currentProcess" rows="2" placeholder="e.g. spreadsheets, paper sign-up sheets, another software, etc."></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">Approximately how many volunteers do you coordinate?</label>
                <input type="text" class="form-control" v-model="form.volunteerCount" placeholder="e.g. 25, 50-100, etc." />
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">What Groups (and how many people per group) do you want to schedule for each Activity or Service?</label>
                <p class="text-muted text-xs mb-2" style="line-height:1.4;">For example: 3 Ushers, 2 Greeters, 5 Choir Members, etc.</p>
                <div v-for="(group, idx) in form.groups" :key="idx" class="d-flex align-items-center gap-2 mb-2">
                  <input type="text" class="form-control form-control-sm" v-model="group.name" placeholder="Group name (e.g. Ushers)" style="flex:2;" />
                  <input type="number" class="form-control form-control-sm" v-model.number="group.count" placeholder="#" min="1" style="flex:0 0 80px;max-width:80px;" />
                  <button type="button" class="btn btn-sm btn-outline-danger px-2 py-1" @click="removeGroup(idx)" v-if="form.groups.length > 1" title="Remove group">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-sm btn-outline-success mt-1" @click="addGroup">
                  <i class="fas fa-plus me-1"></i>Add Group
                </button>
              </div>

              <div class="mb-3">
                <label class="form-label text-sm fw-semibold">Anything else you'd like us to know?</label>
                <textarea class="form-control" v-model="form.additionalInfo" rows="2" placeholder="Any other details about your needs, challenges, or goals..."></textarea>
              </div>
            </div>

            <!-- Terms -->
            <!-- <div class="form-check text-start mb-3">
              <input class="form-check-input" type="checkbox" id="terms" required />
              <label class="form-check-label small text-muted" for="terms">
                I agree to the <a href="/terms" class="text-success">Terms & Conditions</a>
              </label>
            </div> -->

            <!-- hCaptcha (rendered explicitly from mounted()) -->
            <div class="mb-3 d-flex justify-content-center">
              <div ref="hcaptchaSignup"></div>
            </div>
            <small v-if="captchaError" class="text-danger d-block text-center mb-2">{{ captchaError }}</small>

            <!-- Submit -->
            <div class="text-center">
              <material-button class="mt-3 transition-all duration-300 hover:scale-105" variant="gradient"
                color="success" fullWidth size="lg" type="submit"
                :disabled="loading || (hasLocationQuery && (locationLoading || validLocationId === null))">
                <span v-if="!loading">Sign Up</span>
                <span v-else><i class="fas fa-spinner fa-spin"></i> Signing Up...</span>
              </material-button>
            </div>
          </form>
        </div>

        <!-- OTP Verification Form -->
        <div class="card-body" v-if="currentStep === 'otp'">
          <form role="form" @submit.prevent="handleOtpSubmit">
            <div class="mb-3">
              <label class="form-label d-flex align-items-center mb-3">
                Enter 6-digit OTP sent to {{ form.email }}
              </label>
              <div class="d-flex justify-content-between gap-2">
                <input v-for="(digit, index) in otpDigits" :key="index" :ref="el => { if (el) otpInputs[index] = el }"
                  v-model="otpDigits[index]" type="text" maxlength="1" class="form-control text-center"
                  style="width: 50px; height: 50px; font-size: 1.5rem; font-weight: bold;"
                  @input="handleOtpInput(index, $event)" @keydown="handleOtpKeydown(index, $event)"
                  @paste="handleOtpPaste" />
              </div>
            </div>

            <div class="text-center">
              <material-button class="mt-3 transition-all duration-300 hover:scale-105" variant="gradient"
                color="success" fullWidth size="lg" type="submit" :disabled="loading || !isOtpComplete">
                <span v-if="!loading">Verify OTP</span>
                <span v-else><i class="fas fa-spinner fa-spin"></i> Verifying...</span>
              </material-button>
            </div>

            <p v-if="error" class="text-danger text-center mt-3">
              {{ error }}
            </p>

            <div class="text-center mt-3">
              <p class="text-sm text-muted">
                Didn't receive OTP?
                <a href="#" class="text-success fw-bold" @click.prevent="resendOtp">
                  Resend
                </a>
              </p>
            </div>
          </form>
        </div>

        <!-- Footer -->
        <div class="text-center mt-3" v-if="currentStep === 'signup'">
          <p class="text-sm">
            Already have an account?
            <router-link :to="signInTarget" class="text-success fw-bold">
              Sign In
            </router-link>
          </p>
        </div>
      </div>
    </div>

    <ContentModal v-model="showPasswordSentModal" title="Password Sent" size="md" class="modal-quote d-flex" :show-close="true">
      <template #header>
        <h5 class="modal-title mb-0">
          Password sent to your email
        </h5>
      </template>

      <div>
        <div class="mb-4 d-flex gap-1 align-items-center">
          <p class="m-0">
            A temporary password has been sent to:
          </p>
          <p class="mb-0 fw-semibold text-primary">
            {{ form.email }}
          </p>
        </div>

        <div class="border rounded p-3 mb-4">
          <p class="fw-semibold mb-2">
            What to do next
          </p>
          <ol class="mb-0 ps-3">
            <li class="mb-2">Check your email inbox</li>
            <li class="mb-2">Copy the generated password</li>
            <li class="mb-2">Go to the sign-in page</li>
            <li>Log in using your email and the password</li>
          </ol>
        </div>

        <div class="mb-4">
          <small class="">
            Didn't receive the email? Please check your spam or junk folder.
            If it's still missing, you can
            <router-link :to="{ name: 'ForgotPassword' }" class="fw-semibold text-decoration-none text-info">
              reset your password
            </router-link>.
          </small>
        </div>
      </div>

      <template #footer>
        <material-button
          variant="gradient"
          color="success"
          size="md"
          class="my-0"
          @click="goToSignIn"
        >
          Go to Sign In
        </material-button>
      </template>
    </ContentModal>

  </div>
</template>

<script>
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import { mapMutations, mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toast-notification";
import { isValidEmail } from "@/utils/validation";
import api from "@/config/api";
import {
  renderHcaptcha,
  getHcaptchaResponse,
  resetHcaptcha,
  removeHcaptcha,
} from "@/utils/HcaptchaHelper.js";

const body = document.getElementsByTagName("body")[0];
const toast = useToast();

export default {
  name: "sign-up",
  components: {
    MaterialInput,
    MaterialButton,
    MaterialSelect,
    ContentModal,
  },
  data() {
    return {
      loading: false,
      emailError: null,
      mobilePhoneError: null,
      captchaError: null,
      hcaptchaWidgetId: null,
      hcaptchaSitekey: process.env.VUE_APP_HCAPTCHA_SITEKEY || '0454c266-d5fc-443c-8dea-7140009ab354',
      currentStep: "signup", // 'signup' or 'otp'
      otpDigits: ["", "", "", "", "", ""],
      otpInputs: [],
      error: "",
      showPasswordSentModal: false,
      invitation: null,
      invitationLoading: false,
      invitationError: "",
      lastSignupPayload: null,
      publicSkills: [],
      validLocationId: null,
      validLocationToken: null,
      locationDetails: null,
      locationLoading: false,
      locationLoadError: "",
      locationSkillsRequestId: 0,
      form: {
        email: "",
        firstname: "",
        lastname: "",
        mobilephone: "",
        time_zone: "", // auto-detect
        country: "",
        registrationtype: "",
        selectedSkillIds: [],
        orgName: "",
        eventTypes: "",
        currentProcess: "",
        volunteerCount: "",
        heardAboutUs: "",
        additionalInfo: "",
        groups: [{ name: "", count: null }],
      },
    };
  },
  computed: {
    ...mapGetters([
      "stCountries",
      "stTimezones",
      "authError",
      "authLoading",
    ]),
    countryOptions() {
      return Object.keys(this.stCountries).map((key) => ({
        value: key,
        label: this.stCountries[key],
      }));
    },
    timezoneOptions() {
      return Object.keys(this.stTimezones).map((key) => ({
        value: key,
        label: this.stTimezones[key],
      }));
    },
    hasLocationQuery() {
      return Object.prototype.hasOwnProperty.call(this.$route.query, "locid") || Boolean(this.invitation);
    },
    signInTarget() {
      if (this.invitation?.token) {
        return { name: "SignIn", query: { redirect: `/serve-invitation/${this.invitation.token}` } };
      }
      const redirect = this.$route.query.redirect;
      return typeof redirect === "string" && /^\/people-groups\/invitations\/accept\?token=[A-Za-z0-9]+$/.test(redirect)
        ? { name: "SignIn", query: { redirect } }
        : { name: "SignIn" };
    },
    registrationTypeOptions() {
      if (this.hasLocationQuery) {
        return [{ value: "1", label: "Join an organization" }];
      }
      return [
        { value: "1", label: "Join an existing organization" },
        { value: "2", label: "Setup new organization" },
      ];
    },
    heardAboutOptions() {
      return [
        { value: "email marketing", label: "Email marketing" },
        { value: "search engine", label: "Search engine" },
        { value: "another website", label: "Another website" },
        { value: "radio advertisement", label: "Radio advertisement" },
        { value: "social media", label: "Social media" },
        { value: "other", label: "Other" },
      ];
    },
    isOtpComplete() {
      return this.otpDigits.every(digit => digit !== "");
    },
  },
  watch: {
    "$route.query.locid"(value) {
      if (!this.$route.query.invite) {
        this.invitation = null;
        this.loadLocationSkills(value);
      }
    },
    "$route.query.invite"(token) {
      if (token) {
        this.loadInvitation(token);
      } else {
        this.invitation = null;
        this.loadLocationSkills();
      }
    },
    // The captcha div lives inside a `v-if="currentStep === 'signup'"` block,
    // so the DOM node is destroyed when we switch to the OTP step and a new
    // node is created when we come back. Re-render the widget accordingly.
    currentStep(newVal, oldVal) {
      if (newVal === 'signup' && oldVal !== 'signup') {
        this.$nextTick(() => this.initHcaptcha());
      } else if (oldVal === 'signup' && newVal !== 'signup') {
        this.destroyHcaptcha();
      }
    },
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    ...mapActions(["getCountries", "getTimezones", "register", "verifySignupOtp"]),
    async loadLocationSkills(rawLocationId = this.$route.query.locid) {
      const requestId = ++this.locationSkillsRequestId;

      this.publicSkills = [];
      this.validLocationId = null;
      this.validLocationToken = null;
      this.locationDetails = null;
      this.locationLoading = false;
      this.locationLoadError = "";
      this.form.selectedSkillIds = [];
      this.form.country = "";
      this.form.time_zone = "";
      this.form.registrationtype = this.hasLocationQuery ? "1" : "";

      if (!rawLocationId) {
        if (this.hasLocationQuery) {
          this.locationLoadError = "This sign-up link is invalid. Please request a new link from the location administrator.";
        }
        return;
      }
      if (typeof rawLocationId !== "string" || rawLocationId.length > 2048 || !/^pl1_[A-Za-z0-9_-]+$/.test(rawLocationId)) {
        this.locationLoadError = "This sign-up link is invalid. Please request a new link from the location administrator.";
        return;
      }
      this.locationLoading = true;

      try {
        const response = await api.get("/volunteer/skills", {
          params: { locationid: rawLocationId },
        });
        if (requestId !== this.locationSkillsRequestId) {
          return;
        }

        const locationData = response.data?.data;
        const locationId = Number(locationData?.location?.id);
        if (
          response.data?.success !== true
          || !locationData?.location
          || !Number.isSafeInteger(locationId) || locationId <= 0
          || !Array.isArray(locationData?.skills)
        ) {
          this.locationLoadError = "We could not load the organization and Location for this sign-up link.";
          return;
        }
        this.locationDetails = {
          id: locationId,
          name: locationData.location.name ?? "",
          organization_name: locationData.location.organization_name ?? "",
          country: locationData.location.country ?? "",
          time_zone: locationData.location.time_zone ?? "",
        };
        this.form.country = this.locationDetails.country;
        this.form.time_zone = this.locationDetails.time_zone;

        const publicSkills = locationData.skills
          .filter(skill => [true, 1, "1"].includes(skill.publicview))
          .map(skill => ({
            ...skill,
            skillid: Number(skill.skillid ?? skill.id),
            skillname: skill.skillname ?? skill.name,
            preselect: [true, 1, "1"].includes(skill.preselect),
            interest_auto_add: [true, 1, "1"].includes(skill.interest_auto_add ?? skill.autoadd),
          }))
          .filter(skill => Number.isSafeInteger(skill.skillid) && skill.skillid > 0);
        const invitationSkills = (this.invitation?.skills || []).map(skill => ({
          skillid: Number(skill.id),
          skillname: skill.name,
          description: skill.description || "",
          publicview: Boolean(skill.publicview),
          preselect: false,
          interest_auto_add: false,
          invite_suggested: true,
        })).filter(skill => Number.isSafeInteger(skill.skillid) && skill.skillid > 0);
        const invitationSkillIds = new Set(invitationSkills.map(skill => skill.skillid));
        this.publicSkills = publicSkills.map(skill => ({
          ...skill,
          invite_suggested: invitationSkillIds.has(skill.skillid),
        }));
        const knownSkillIds = new Set(this.publicSkills.map(skill => skill.skillid));
        this.publicSkills.push(...invitationSkills.filter(skill => !knownSkillIds.has(skill.skillid)));
        this.validLocationId = locationId;
        this.validLocationToken = rawLocationId;
        this.form.selectedSkillIds = [...new Set([
          ...this.publicSkills.filter(skill => skill.preselect).map(skill => skill.skillid),
          ...invitationSkills.map(skill => skill.skillid),
        ])];
      } catch (error) {
        if (requestId === this.locationSkillsRequestId) {
          this.publicSkills = [];
          this.validLocationId = null;
          this.validLocationToken = null;
          this.locationDetails = null;
          this.locationLoadError = "We could not load this Location. Please check the sign-up link and try again.";
        }
      } finally {
        if (requestId === this.locationSkillsRequestId) {
          this.locationLoading = false;
        }
      }
    },
    async loadInvitation(token = this.$route.query.invite) {
      this.invitation = null;
      this.invitationError = "";
      if (typeof token !== "string" || !/^[a-f0-9]{64}$/.test(token)) {
        this.invitationError = "This invitation link is invalid. Please ask your friend to send it again.";
        return;
      }

      this.invitationLoading = true;
      try {
        const response = await api.get(`/volunteer-invitations/token/${encodeURIComponent(token)}`);
        const invitation = response.data?.data;
        if (!response.data?.success || !invitation?.location_token) {
          throw new Error("This invitation link is unavailable.");
        }
        // Someone who already has an account, or whose invitation was already used, gets the regular
        // sign-up page for the Location with nothing pre-populated.
        if (invitation.recipient_exists || invitation.already_registered) {
          await this.showRegularLocationSignUp(invitation.location_token);
          return;
        }
        this.invitation = { ...invitation, token };
        this.form.firstname = invitation.firstname || "";
        this.form.lastname = invitation.lastname || "";
        this.form.email = invitation.email || "";
        await this.loadLocationSkills(invitation.location_token);
        if (this.locationLoadError) {
          this.invitationError = this.locationLoadError;
        }
      } catch (error) {
        // An expired invitation still identifies its Location, so offer that Location's regular sign-up page.
        const fallbackLocationToken = error.response?.data?.location_token;
        if (fallbackLocationToken) {
          await this.showRegularLocationSignUp(fallbackLocationToken);
          return;
        }
        this.invitationError = error.response?.data?.message || error.message || "This invitation link is unavailable.";
      } finally {
        this.invitationLoading = false;
      }
    },
    async showRegularLocationSignUp(locationToken) {
      this.invitation = null;
      const query = { locid: locationToken };
      if (typeof this.$route.query.redirect === "string") {
        query.redirect = this.$route.query.redirect;
      }
      await this.$router.replace({ name: "SignUp", query });
    },
    addGroup() {
      this.form.groups.push({ name: "", count: null });
    },
    removeGroup(idx) {
      this.form.groups.splice(idx, 1);
    },
    async initHcaptcha() {
      // Clean up any previous instance (e.g. when remounting after OTP step).
      this.destroyHcaptcha();
      const container = this.$refs.hcaptchaSignup;
      if (!container) return;
      try {
        this.hcaptchaWidgetId = await renderHcaptcha(container, this.hcaptchaSitekey, {
          callback: () => {
            this.captchaError = null;
          },
          'expired-callback': () => {
            this.captchaError = 'Captcha expired. Please solve it again.';
          },
          'error-callback': () => {
            this.captchaError = 'Captcha failed to load. Please refresh and try again.';
          },
        });
      } catch (e) {
        console.error('Failed to render hCaptcha on sign-up:', e);
        this.captchaError = 'Captcha failed to load. Please disable ad blockers or refresh the page.';
      }
    },
    destroyHcaptcha() {
      removeHcaptcha(this.hcaptchaWidgetId);
      this.hcaptchaWidgetId = null;
    },
    isNorthAmericanCountry() {
      const country = (this.form.country || '').toUpperCase();
      return country === 'US' || country === 'CA'
        || country === 'UNITED STATES' || country === 'CANADA';
    },
    validateAndFormatMobilePhone() {
      const raw = this.form.mobilephone || '';
      const digits = raw.replace(/\D/g, '');

      // Empty is fine — mobile is optional
      if (digits.length === 0) {
        this.mobilePhoneError = null;
        return;
      }

      if (this.isNorthAmericanCountry()) {
        if (digits.length < 10) {
          this.mobilePhoneError = 'Mobile number must be at least 10 digits for US/Canada.';
          return;
        }
        if (digits.length === 10) {
          // Auto-prepend country code 1
          this.form.mobilephone = '1' + digits;
          this.mobilePhoneError = null;
          return;
        }
        if (digits.length === 11 && digits.startsWith('1')) {
          this.form.mobilephone = digits;
          this.mobilePhoneError = null;
          return;
        }
        // Anything else is invalid
        this.mobilePhoneError = 'US/Canada numbers must be 1 followed by 10 digits (e.g. 12125551234).';
        return;
      }

      // Non-North-American: just store digits, no strict validation for now
      this.form.mobilephone = digits;
      this.mobilePhoneError = null;
    },
    validateEmail() {
      if (this.form.email && this.form.email.trim() !== '') {
        if (!isValidEmail(this.form.email)) {
          this.emailError = 'Please enter a valid email address';
        } else {
          this.emailError = null;
        }
      } else {
        this.emailError = null;
      }
    },
    async handleSubmit() {
      // Validate email and mobile phone before submission
      this.validateEmail();
      this.validateAndFormatMobilePhone();

      if (this.emailError || this.mobilePhoneError) {
        toast.error("Please fix validation errors before submitting", { position: "top-right" });
        return;
      }
      const expectedLocationToken = this.invitation?.location_token || this.$route.query.locid;
      if (this.hasLocationQuery && (this.validLocationId === null
        || this.validLocationToken !== expectedLocationToken || this.locationLoading)) {
        const message = this.locationLoading
          ? "Location details are still loading. Please wait and try again."
          : this.locationLoadError || "Unable to load the Location for this sign-up link.";
        toast.error(message, { position: "top-right" });
        return;
      }
      if (this.validLocationId !== null) {
        this.form.registrationtype = "1";
      }

      // Validate hCaptcha
      if (this.hcaptchaWidgetId === null) {
        this.captchaError = 'Captcha is still loading. Please wait a moment and try again, or refresh the page if it never appears (ad blockers can prevent it from loading).';
        return;
      }
      const hcaptchaResponse = getHcaptchaResponse(this.hcaptchaWidgetId);
      if (!hcaptchaResponse) {
        this.captchaError = 'Please complete the captcha verification';
        return;
      }
      this.captchaError = null;

      // Validate registration details if setting up new org
      if (this.form.registrationtype === '2') {
        if (!this.form.orgName?.trim() || !this.form.eventTypes?.trim() || !this.form.currentProcess?.trim()) {
          toast.error("Please fill in the required Registration Details fields", { position: "top-right" });
          return;
        }
      }

      this.loading = true;
      this.error = "";

      // Consolidate registration details into sign_up_context
      const payload = { ...this.form, h_captcha_response: hcaptchaResponse };
      delete payload.selectedSkillIds;
      delete payload.heardAboutUs;
      if (this.form.registrationtype === '2') {
        const groupLines = this.form.groups
          .filter(g => g.name && g.name.trim())
          .map(g => `  ${g.count || '?'} ${g.name.trim()}`)
          .join('\n');
        payload.sign_up_context = [
          `Organization Name: ${this.form.orgName || 'N/A'}`,
          `Events/Services: ${this.form.eventTypes || 'N/A'}`,
          `Current Process: ${this.form.currentProcess || 'N/A'}`,
          `Volunteer Count: ${this.form.volunteerCount || 'N/A'}`,
          `Groups:\n${groupLines || '  N/A'}`,
          `Additional Info: ${this.form.additionalInfo || 'N/A'}`,
          `How did you hear about churchscheduling.com?: ${this.form.heardAboutUs || 'N/A'}`,
        ].join('\n');
      }
      if (this.form.registrationtype === '1' && this.validLocationId !== null) {
        payload.locid = this.validLocationId;
        payload.location_token = this.validLocationToken;
        payload.registrationtype = "1";
        payload.country = this.locationDetails?.country || null;
        payload.time_zone = this.locationDetails?.time_zone || null;
        payload.selected_skill_ids = this.publicSkills
          .filter(skill => skill.preselect || this.form.selectedSkillIds.some(id => Number(id) === skill.skillid))
          .map(skill => skill.skillid);
        if (this.invitation?.token) {
          payload.invitation_token = this.invitation.token;
        }
      }

      try {
        this.lastSignupPayload = { ...payload };
        const response = await this.register(payload);
        if (response.status === 422) {

          console.log("Registration response:", response);
        }
        toast.success("OTP sent to your email address", { position: "top-right" });
        // Move to OTP verification step
        this.currentStep = "otp";
        // Focus first OTP input
        this.$nextTick(() => {
          if (this.otpInputs[0]) {
            this.otpInputs[0].focus();
          }
        });
      } catch (error) {
        console.log("error", error);

        // Reset the captcha so the user can submit again without reloading.
        resetHcaptcha(this.hcaptchaWidgetId);

        // Handle validation errors (422)
        if (error.type === "validation" && error.messages && error.messages.length > 0) {
          // Messages are already shown by the API interceptor, but we can also set local error
          this.error = error.messages.join(", ");
        } else {
          // Handle other errors
          const errorMessage = error.response?.data?.message || error.message || "Registration failed. Please try again.";
          this.error = errorMessage;
          toast.error(errorMessage, { position: "top-right" });
        }
      } finally {
        this.loading = false;
      }
    },
    handleOtpInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, "");
      this.otpDigits[index] = value;

      // Auto-focus next input
      if (value && index < 5) {
        this.$nextTick(() => {
          if (this.otpInputs[index + 1]) {
            this.otpInputs[index + 1].focus();
          }
        });
      }
    },
    handleOtpKeydown(index, event) {
      // Handle backspace
      if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
        this.$nextTick(() => {
          if (this.otpInputs[index - 1]) {
            this.otpInputs[index - 1].focus();
          }
        });
      }

      // Handle arrow keys
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.$nextTick(() => {
          if (this.otpInputs[index - 1]) {
            this.otpInputs[index - 1].focus();
          }
        });
      }

      if (event.key === "ArrowRight" && index < 5) {
        event.preventDefault();
        this.$nextTick(() => {
          if (this.otpInputs[index + 1]) {
            this.otpInputs[index + 1].focus();
          }
        });
      }
    },
    handleOtpPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);

      for (let i = 0; i < 6; i++) {
        this.otpDigits[i] = pastedData[i] || "";
      }

      // Focus the next empty input or the last one
      const nextEmptyIndex = this.otpDigits.findIndex(digit => digit === "");
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      this.$nextTick(() => {
        if (this.otpInputs[focusIndex]) {
          this.otpInputs[focusIndex].focus();
        }
      });
    },
    async handleOtpSubmit() {
      if (!this.isOtpComplete) {
        this.error = "Please enter complete OTP";
        return;
      }

      this.error = "";
      this.loading = true;

      // Combine OTP digits
      const otp = this.otpDigits.join("");

      try {
        await this.verifySignupOtp({
          email: this.form.email,
          otp: otp
        });

        // Show success message
        toast.success("Email verified successfully! Password has been sent to your email.", { position: "top-right" });

        // Reset to signup step to show registration form and hide OTP form
        this.currentStep = "signup";
        
        // Clear OTP inputs
        this.otpDigits = ["", "", "", "", "", ""];
        this.error = "";

        // Show the password sent instructions modal
        this.$nextTick(() => {
          this.showPasswordSentModal = true;
        });
      } catch (error) {
        console.error("OTP verification failed:", error);
        const errorMessage = error.response?.data?.message || error.message || "Invalid OTP. Please try again.";
        this.error = errorMessage;
        toast.error(errorMessage, { position: "top-right" });

        // Clear OTP inputs on error
        this.otpDigits = ["", "", "", "", "", ""];
        this.$nextTick(() => {
          if (this.otpInputs[0]) {
            this.otpInputs[0].focus();
          }
        });
      } finally {
        this.loading = false;
      }
    },
    async resendOtp() {
      // Reset OTP inputs
      this.otpDigits = ["", "", "", "", "", ""];
      this.error = "";
      this.loading = true;

      try {
        // Resend OTP by calling register again
        await this.register(this.lastSignupPayload || this.form);

        toast.success("OTP resent to your email address", { position: "top-right" });

        this.$nextTick(() => {
          if (this.otpInputs[0]) {
            this.otpInputs[0].focus();
          }
        });
      } catch (error) {
        console.error("Resend OTP error:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to resend OTP. Please try again.";
        this.error = errorMessage;
        toast.error(errorMessage, { position: "top-right" });
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push(this.signInTarget);
    },
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.locationSkillsRequestId++;
    this.destroyHcaptcha();
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.add("bg-gray-100");
  },
  async mounted() {
    await this.getCountries();
    await this.getTimezones();
    if (this.$route.query.invite) {
      await this.loadInvitation(this.$route.query.invite);
    } else {
      this.loadLocationSkills();
    }
    // Render the captcha on initial mount (currentStep starts as 'signup').
    this.initHcaptcha();
  },
};
</script>

<style scoped>
.signup-location-context {
  min-height: 76px;
  padding: 1rem 1.15rem;
  border: 1px solid #cfe8d5;
  border-radius: 14px;
  background: linear-gradient(125deg, #f0fdf4, #f8fafc);
  box-shadow: 0 4px 14px rgba(25, 135, 84, 0.08);
}

.signup-invitation-note {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.9rem 1rem;
  border: 1px solid #d5e8d9;
  border-radius: 8px;
  background: #f1f8f2;
  color: #405648;
  font-size: 0.84rem;
  line-height: 1.55;
}

.signup-invitation-note-icon,.invite-existing-mark {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border-radius: 50%;
  background: #e0f0e4;
  color: #28704b;
}

.signup-invitation-note-icon .material-icons { font-size: 19px; }
.signup-invitation-personal-note { margin-top: 0.35rem; color: #68786d; white-space: pre-line; }
.invite-existing-mark { margin: 0 auto 1rem; width: 48px; height: 48px; }
.invite-existing-mark .material-icons { font-size: 23px; }

.signup-location-icon {
  display: flex;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #198754;
  color: #fff;
}

.signup-location-icon .material-icons {
  font-size: 24px;
}

.signup-location-copy h5 {
  color: #205a37;
  font-size: 1rem;
  font-weight: 700;
}

.signup-location-eyebrow {
  color: #64806d;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.signup-location-name {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  color: #52645a;
  font-size: 0.82rem;
}

.signup-location-name .material-icons {
  font-size: 16px;
}

.signup-skill-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.signup-skill-fieldset legend {
  float: none;
  width: auto;
  padding: 0;
}

.signup-skill-options {
  max-height: 260px;
  overflow-y: auto;
  padding: 0.45rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background: #fff;
}

.signup-skill-option {
  margin: 0;
  padding: 0.6rem 0;
  border-bottom: 1px solid #edf0f2;
}

.signup-skill-option:last-child {
  border-bottom: 0;
}

.signup-skill-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  cursor: pointer;
}

.signup-skill-option .form-check-input:disabled {
  opacity: 0.85;
}
.form-control:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

/* Password sent modal sizing */
:deep(.modal-dialog.modal-md) {
  min-width: auto;
  max-width: 600px;
  width: calc(100vw - 2rem);
  min-height: auto;
  height: auto;
  margin: 1rem auto;
  display: flex;
  flex-direction: column;
}

:deep(.modal-dialog.modal-md .modal-content) {
  min-height: auto;
  height: auto;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

:deep(.modal-dialog.modal-md .modal-body) {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

:deep(.modal-dialog.modal-md .modal-footer) {
  flex-shrink: 0;
}
</style>
